import { useEffect, useState } from "react";
import { getClientBuildId } from "../buildVersion";

const DIAGNOSTICS_QUERY_KEY = "viewrDiagnostics";
const DEV_DEPLOY_TIERS = new Set(["dev", "dev2", "development"]);

export interface PwaDiagnosticSnapshot {
  clientBuildId: string;
  serverBuildId: string;
  unityGuid: string;
  serviceWorkerActive: string;
  serviceWorkerWaiting: string;
  serviceWorkerController: string;
  unityInputProfile: string;
  displayMode: "browser" | "standalone";
}

const unavailable = "unavailable";

export const isPwaDiagnosticsEnabled = ({
  deployTier = process.env.REACT_APP_DEPLOY_TIER ?? "",
  isDevelopment = process.env.NODE_ENV === "development",
  search = window.location.search,
}: {
  deployTier?: string;
  isDevelopment?: boolean;
  search?: string;
} = {}): boolean => {
  const isDevDeploy =
    isDevelopment || DEV_DEPLOY_TIERS.has(deployTier.trim().toLowerCase());
  return (
    isDevDeploy &&
    new URLSearchParams(search).get(DIAGNOSTICS_QUERY_KEY) === "1"
  );
};

const readText = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) return unavailable;
    return (await response.text()).trim() || unavailable;
  } catch {
    return unavailable;
  }
};

const readServerBuildId = async (): Promise<string> => {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!response.ok) return unavailable;
    const data = (await response.json()) as { buildId?: string };
    return data.buildId?.trim() || unavailable;
  } catch {
    return unavailable;
  }
};

export const collectPwaDiagnosticSnapshot =
  async (): Promise<PwaDiagnosticSnapshot> => {
    const serviceWorker =
      typeof navigator !== "undefined" && "serviceWorker" in navigator
        ? navigator.serviceWorker
        : undefined;
    let registration: ServiceWorkerRegistration | undefined;
    try {
      registration = await serviceWorker?.getRegistration();
    } catch {
      registration = undefined;
    }

    const [serverBuildId, unityGuid] = await Promise.all([
      readServerBuildId(),
      readText(`/unity/GUID.txt?t=${Date.now()}`),
    ]);

    return {
      clientBuildId: getClientBuildId() || unavailable,
      serverBuildId,
      unityGuid,
      serviceWorkerActive: registration?.active?.scriptURL || unavailable,
      serviceWorkerWaiting: registration?.waiting?.scriptURL || "none",
      serviceWorkerController:
        serviceWorker?.controller?.scriptURL || unavailable,
      unityInputProfile:
        document.documentElement.dataset.viewrUnityInputProfile || unavailable,
      displayMode:
        typeof window.matchMedia === "function" &&
        window.matchMedia("(display-mode: standalone)").matches
          ? "standalone"
          : "browser",
    };
  };

export default function PwaDiagnostics() {
  const [snapshot, setSnapshot] = useState<PwaDiagnosticSnapshot | null>(null);

  useEffect(() => {
    if (!isPwaDiagnosticsEnabled()) return;
    let active = true;
    void collectPwaDiagnosticSnapshot().then((nextSnapshot) => {
      if (active) setSnapshot(nextSnapshot);
    });
    const observer = new MutationObserver(() => {
      const unityInputProfile =
        document.documentElement.dataset.viewrUnityInputProfile || unavailable;
      setSnapshot((current) =>
        current ? { ...current, unityInputProfile } : current,
      );
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-viewr-unity-input-profile"],
    });
    return () => {
      active = false;
      observer.disconnect();
    };
  }, []);

  if (!isPwaDiagnosticsEnabled()) return null;

  return (
    <aside
      aria-label="View R diagnostics"
      style={{
        position: "fixed",
        right: 8,
        bottom: 8,
        zIndex: 2147483647,
        width: "min(360px, calc(100vw - 16px))",
        padding: 12,
        border: "1px solid rgba(255,255,255,0.2)",
        borderRadius: 8,
        background: "rgba(18,18,20,0.94)",
        color: "#fff",
        font: "11px/1.45 monospace",
        overflowWrap: "anywhere",
        boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
      }}
    >
      <strong style={{ display: "block", marginBottom: 6 }}>
        View R dev diagnostics
      </strong>
      {snapshot ? (
        <>
          <div>Client: {snapshot.clientBuildId}</div>
          <div>Server: {snapshot.serverBuildId}</div>
          <div>Unity: {snapshot.unityGuid}</div>
          <div>Unity input: {snapshot.unityInputProfile}</div>
          <div>Mode: {snapshot.displayMode}</div>
          <div>SW controller: {snapshot.serviceWorkerController}</div>
          <div>SW active: {snapshot.serviceWorkerActive}</div>
          <div>SW waiting: {snapshot.serviceWorkerWaiting}</div>
        </>
      ) : (
        <div>Reading runtime state...</div>
      )}
    </aside>
  );
}
